import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  UserPlus, 
  Crown, 
  Shield, 
  User,
  Building2
} from 'lucide-react';
import InviteMemberDialog from '@/components/team/InviteMemberDialog';
import MemberPermissionsDialog from '@/components/team/MemberPermissionsDialog';
import MemberCard from '@/components/team/MemberCard';

export default function TeamSettingsContent() {
  const { user } = useAuth();
  const { 
    organization, 
    members,
    currentMember,
    memberPermissions,
    dataAssignments,
    loading, 
    isOwner,
    canManageMembers,
    removeMember,
    updateMemberRole,
    updateMemberPermissions,
    updateDataAssignments,
    updateOrganizationName,
    updateMemberName,
  } = useOrganization();
  
  // Security: Get current user's role for conditional rendering of sensitive data
  const currentUserRole = currentMember?.role;

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameMember, setEditingNameMember] = useState<{ id: string; name: string } | null>(null);
  const [savingName, setSavingName] = useState(false);

  const activeMembers = members.filter(m => m.accepted_at || m.role === 'owner');
  const pendingMembers = members.filter(m => !m.accepted_at && m.role !== 'owner');
  const memberCount = activeMembers.length;
  const maxMembers = organization?.max_members || 3;

  const handleSaveOrgName = async () => {
    if (orgName.trim() && organization) {
      await updateOrganizationName(orgName.trim());
      setIsEditingName(false);
    }
  };

  const handleSaveMemberName = async () => {
    if (editingNameMember && editingNameMember.name.trim()) {
      setSavingName(true);
      const success = await updateMemberName(editingNameMember.id, editingNameMember.name.trim());
      setSavingName(false);
      if (success) {
        setEditingNameMember(null);
      }
    }
  };

  const handleEditName = (memberId: string, currentName: string) => {
    setEditingNameMember({ id: memberId, name: currentName });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle>Organização</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEditingName ? (
            <div className="flex gap-2">
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Nome da organização"
              />
              <Button onClick={handleSaveOrgName}>Salvar</Button>
              <Button variant="outline" onClick={() => setIsEditingName(false)}>
                Cancelar
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{organization?.name || 'Minha Empresa'}</p>
                <p className="text-sm text-muted-foreground">
                  {memberCount} de {maxMembers} usuários
                </p>
              </div>
              {isOwner && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setOrgName(organization?.name || '');
                    setIsEditingName(true);
                  }}
                >
                  Editar nome
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle>Membros da Equipe</CardTitle>
            </div>
            {canManageMembers && memberCount < maxMembers && (
              <Button onClick={() => setIsInviteOpen(true)} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Convidar Usuário
              </Button>
            )}
          </div>
          <CardDescription>
            {canManageMembers 
              ? 'Gerencie os usuários que têm acesso à sua organização'
              : 'Veja os membros da sua organização'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Active Members */}
          {activeMembers.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              currentUserId={user?.id || ''}
              canManage={canManageMembers}
              permissions={memberPermissions[member.id] || []}
              currentUserRole={currentUserRole}
              onRemove={() => removeMember(member.id)}
              onEditPermissions={() => setEditingMemberId(member.id)}
              onRoleChange={(role) => updateMemberRole(member.id, role)}
              onEditName={handleEditName}
            />
          ))}

          {/* Pending Invites */}
          {pendingMembers.length > 0 && (
            <>
              <Separator className="my-4" />
              <p className="text-sm font-medium text-muted-foreground">
                Convites Pendentes
              </p>
              {pendingMembers.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  currentUserId={user?.id || ''}
                  canManage={canManageMembers}
                  permissions={[]}
                  isPending
                  currentUserRole={currentUserRole}
                  onRemove={() => removeMember(member.id)}
                  onEditName={handleEditName}
                />
              ))}
            </>
          )}

          {members.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Nenhum membro encontrado
            </p>
          )}
        </CardContent>
      </Card>

      {/* Permissions Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legenda de Funções</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-2">
              <Crown className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Proprietário</p>
                <p className="text-sm text-muted-foreground">
                  Acesso total. Gerencia membros e configurações.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Administrador</p>
                <p className="text-sm text-muted-foreground">
                  Acesso total aos dados, mas não gerencia membros.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <User className="h-5 w-5 text-gray-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Membro</p>
                <p className="text-sm text-muted-foreground">
                  Acesso limitado conforme permissões definidas.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <InviteMemberDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        maxMembers={maxMembers}
        currentCount={memberCount}
      />

      {editingMemberId && (
        <MemberPermissionsDialog
          open={!!editingMemberId}
          onOpenChange={(open) => !open && setEditingMemberId(null)}
          memberId={editingMemberId}
          member={members.find(m => m.id === editingMemberId)}
          permissions={memberPermissions[editingMemberId] || []}
          dataAssignments={dataAssignments[editingMemberId] || []}
          onSavePermissions={(perms) => updateMemberPermissions(editingMemberId, perms)}
          onSaveAssignments={(table, ids) => updateDataAssignments(editingMemberId, table, ids)}
        />
      )}

      {/* Edit Name Dialog */}
      <Dialog open={!!editingNameMember} onOpenChange={(open) => !open && setEditingNameMember(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Editar Nome do Usuário</DialogTitle>
            <DialogDescription>
              Altere o nome de exibição do membro.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="member-name">Nome</Label>
              <Input
                id="member-name"
                value={editingNameMember?.name || ''}
                onChange={(e) => setEditingNameMember(prev => prev ? { ...prev, name: e.target.value } : null)}
                placeholder="Digite o nome"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNameMember(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveMemberName} disabled={savingName || !editingNameMember?.name.trim()}>
              {savingName ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}